import type {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type { IServiceProviderPhoenixApi } from './api';
import { ServiceProviderPhoenixApi } from './api';
import type {
  PostServiceProviderDto,
  ServiceProviderListDto,
  ServiceProviderWithStatementDescriptorDto,
} from '@pflegenavi/shared/api';
import type { PaginatedResultSet } from '@pflegenavi/shared/utils';

const ApiContext = createContext<IServiceProviderPhoenixApi | undefined>(
  undefined
);
const {
  useApi: useServiceProviderPhoenixApi,
  ApiProvider: ServiceProviderPhoenixApiProvider,
} = makeApiProvider({
  name: 'ServiceProviderPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ServiceProviderPhoenixApi(tenantId, auth, apiUrl),
});

export {
  useServiceProviderPhoenixApi,
  ServiceProviderPhoenixApiProvider,
  ApiContext as ServiceProviderPhoenixApiContext,
  IServiceProviderPhoenixApi,
};

const SERVICE_PROVIDER_PHOENIX_KEY = (serviceProviderId: string) => [
  'service-provider-phoenix',
  serviceProviderId,
];

const ADD_SERVICE_PROVIDER_KEY: QueryKey = ['add-service-provider-phoenix'];
const INVITE_SERVICE_PROVIDER_KEY: QueryKey = [
  'invite-service-provider-phoenix',
];
const REVOKE_SERVICE_PROVIDER_INVITE_KEY: QueryKey = [
  'revoke-service-provider-invite-phoenix',
];
const UPDATE_SERVICE_PROVIDER_STATEMENT_DESCRIPTOR_KEY: QueryKey = [
  'update-service-provider-statement-descriptor-phoenix',
];

export const useAddServiceProviderPhoenix = ({
  nursingHomeId,
}: {
  nursingHomeId: string | undefined;
}): UseMutationResult<
  { id: string },
  Error,
  { data: PostServiceProviderDto }
> => {
  const api = useServiceProviderPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, Error, { data: PostServiceProviderDto }>({
    mutationKey: ADD_SERVICE_PROVIDER_KEY,
    mutationFn: ({ data }) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.addServiceProvider({ nursingHomeId, data });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['serviceProviders'] });
      await queryClient.invalidateQueries({
        queryKey: ['service-providers-phoenix-list'],
      });
    },
  });
};

export const useInviteServiceProvider = (
  serviceProviderId: string
): UseMutationResult<{ success: boolean }, Error, { email: string }> => {
  const api = useServiceProviderPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { email: string }>({
    mutationKey: INVITE_SERVICE_PROVIDER_KEY,
    mutationFn: ({ email }) =>
      api.inviteServiceProvider({ serviceProviderId, email }),
    onSettled: async () => {
      return await queryClient.invalidateQueries({
        queryKey: SERVICE_PROVIDER_PHOENIX_KEY(serviceProviderId),
      });
    },
  });
};

export const useRevokeServiceProviderInvite = (
  serviceProviderId: string
): UseMutationResult<
  { data: ServiceProviderWithStatementDescriptorDto },
  Error,
  void
> => {
  const api = useServiceProviderPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    { data: ServiceProviderWithStatementDescriptorDto },
    Error,
    void
  >({
    mutationKey: REVOKE_SERVICE_PROVIDER_INVITE_KEY,
    mutationFn: () => api.revokeServiceProviderInvite({ serviceProviderId }),
    onSettled: async () => {
      return await queryClient.invalidateQueries({
        queryKey: SERVICE_PROVIDER_PHOENIX_KEY(serviceProviderId),
      });
    },
  });
};

export const useGetServiceProviderPhoenix = (
  serviceProviderId: string | undefined,
  options?: Omit<
    UseQueryOptions<
      { data: ServiceProviderWithStatementDescriptorDto },
      unknown
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  { data: ServiceProviderWithStatementDescriptorDto },
  unknown
> => {
  return useApiQuery(
    useServiceProviderPhoenixApi,
    SERVICE_PROVIDER_PHOENIX_KEY(serviceProviderId ?? ''),
    (api) => {
      if (!serviceProviderId) {
        throw new Error('service provider is required');
      }
      return api.getServiceProvider(serviceProviderId);
    },
    {
      enabled: serviceProviderId !== undefined,
      ...options,
    }
  );
};

export const useUpdateServiceProviderStatementDescriptor = (
  serviceProviderId?: string
): UseMutationResult<
  { success: boolean },
  Error,
  { data: { statement_descriptor: string } }
> => {
  const api = useServiceProviderPhoenixApi();
  const queryClient = useQueryClient();

  if (!serviceProviderId) {
    throw new Error('ServiceProviderId are required ');
  }

  return useMutation<
    { success: boolean },
    Error,
    { data: { statement_descriptor: string } }
  >({
    mutationKey: UPDATE_SERVICE_PROVIDER_STATEMENT_DESCRIPTOR_KEY,
    mutationFn: ({ data }) =>
      api.updateServiceProviderStatementDescriptor({ serviceProviderId, data }),
    onSuccess: async () => {
      return await queryClient.invalidateQueries({
        queryKey: SERVICE_PROVIDER_PHOENIX_KEY(serviceProviderId),
      });
    },
  });
};

export const useListServiceProvidersPhoenix = (
  params: {
    nursingHomeId: string | undefined;
    page: number;
    pageSize: number;
    receiptTypeId?: string;
  },
  options?: Omit<
    UseQueryOptions<PaginatedResultSet<ServiceProviderListDto>, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<PaginatedResultSet<ServiceProviderListDto>, unknown> => {
  return useApiQuery(
    useServiceProviderPhoenixApi,
    ['service-providers-phoenix-list', params],
    (api) => {
      if (!params.nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.listServiceProviders({
        nursingHomeId: params.nursingHomeId,
        page: params.page,
        pageSize: params.pageSize,
        receiptTypeId: params.receiptTypeId,
      });
    },
    {
      enabled: params.nursingHomeId !== undefined,
      ...options,
    }
  );
};
